import { downloadDataUrl } from './download';

/**
 * Decides whether to surface the native share sheet vs. a file download.
 *
 * Feature-detecting `navigator.share` alone isn't enough — Chrome on
 * macOS/Windows ships with the Share API since Chrome 89, but desktop users
 * still expect a Download button there. Combining `(pointer: coarse)` with
 * the API check gates this to actual touch-primary devices (phones / tablets)
 * while letting Chromebook tablets in but keeping desktop Chrome out.
 */
export function shouldUseNativeShare(): boolean {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
  if (typeof navigator.share !== 'function') return false;
  return window.matchMedia('(pointer: coarse)').matches;
}

/**
 * Tries the native share sheet on touch-primary devices; falls back to a
 * file download otherwise (and if share fails for any non-cancellation
 * reason).
 */
export async function shareOrDownload(
  dataUrl: string,
  filename: string,
  shareData: ShareData = {},
): Promise<void> {
  if (shouldUseNativeShare()) {
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], filename, { type: blob.type });
      if (navigator.canShare?.({ files: [file] }) ?? true) {
        await navigator.share({ files: [file], ...shareData });
        return;
      }
    } catch (err) {
      // User dismissed the share sheet — don't also force a download.
      if (err instanceof DOMException && err.name === 'AbortError') return;
      // Fall through to download for other failures.
    }
  }
  downloadDataUrl(dataUrl, filename);
}
